import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import '../styles/_voice-over-v2.scss';
import _isEmpty from 'lodash.isempty';
import _debounce from 'lodash.debounce';
import _uniq from 'lodash.uniq';
import { PROJECT_TYPE } from '@shootsta/common-domain-data';
import { Input, Link, Text } from '../../../../../../ui-kit';
import ManageVoiceOverArtists from '../../../../../ManageVoiceOverArtists/containers/ManageVoiceOverArtists';
import { getVisualBuilderScriptsWordCount } from '../../../AddonManagementModal/utils';
import UploadVoiceOver from './UploadVoiceOver';
import AssetDetailsModal from './AssetDetailsModal/containers/AssetDetailsModal';
import ToggleValueAddon from '../../components/ToggleValueAddon';
import { CURRENCY_BY_ENTITY_CODE, CURRENCY_SYMBOLS } from '../../../../../../common/constants';





















export default function VoiceOverArtistsV2({
  video,
  voiceOverArtists,
  addFilesToQueue,
  organisationId,
  getAssets,
  assetsMap,
  canUpdateVideo,
  resetAddonValuesByType,
  updateProject,
  projectAssets = [],
  priceVersion = '',
  onChangeAddon,
  updateVideoAtomic,
  onOutputsSave,
  getBillingEntityCode,
  onInfoClick,
  customAddonCosts
}) {
  const { outputs, projectType } = video;
  const masterOutput = outputs.find((output) => output.isMaster);
  const {
    voiceOverCustomAssets = [],
    voiceOverInstructions: instructions = ''
  } = masterOutput || {};

  const isAnimationProject = projectType === PROJECT_TYPE.ANIMATION;

  const billingCode = useMemo(
    () => getBillingEntityCode ? getBillingEntityCode() : 'AUS',
    [getBillingEntityCode]
  );

  const currencyCode = CURRENCY_BY_ENTITY_CODE[billingCode];

  const [voiceOverInstructions, setVoiceOverInstructions] = useState(
    instructions
  );
  const [visualBuilderScripts, setVisualBuilderScripts] = useState([]);

  useEffect(() => {
    const { animationOutput = {}, videoScripts = [] } = video || {};
    let scripts = videoScripts && videoScripts.length ? videoScripts : [];

    if (isAnimationProject) {
      const { animationScripts = [] } = animationOutput || {};
      scripts = animationScripts;
    }

    setVisualBuilderScripts(scripts || []);
  }, []);

  function extractAmount(inputString) {
    const currencySymbol = CURRENCY_SYMBOLS[currencyCode] || '';
    const regex = new RegExp(`\\${currencySymbol}\\s*([0-9]+)`);
    const match = inputString.match(regex);
    return match ? parseInt(match[1], 10) : 0;
  }

  function calculateTotal(addons) {
    return addons.reduce(
      (total, current) => total + extractAmount(current.cost),
      0
    );
  }

  const shouldVoiceOverArtistEnabled = () => {
    if (
    !_isEmpty(voiceOverArtists) ||
    !_isEmpty(voiceOverInstructions) ||
    !_isEmpty(voiceOverCustomAssets))
    return true;
    return false;
  };

  const [artistsPanelVisible, setArtistsPanelVisible] = useState(
    shouldVoiceOverArtistEnabled()
  );
  const [selectedVoiceOverArtists, setSelectedVoiceOverArtists] = useState(
    voiceOverArtists || []
  );

  const [customAssets, setCustomAssets] = useState(voiceOverCustomAssets || []);

  const [addonsTotal, setAddonsTotal] = useState(
    calculateTotal(voiceOverArtists)
  );
  const [selectedCustomAssetDetail, setSelectedCustomAssetDetail] = useState({
    assetDetail: null,
    additionalInfo: ''
  });
  const [showAssetDetailsModal, setShowAssetDetailsModal] = useState(false);
  const fileRef = React.useRef([]);
  const scriptWordCount = getVisualBuilderScriptsWordCount(
    visualBuilderScripts
  );

  const handleSelectVO = (selectedVO) => {
    onChangeAddon(selectedVO);
    setSelectedVoiceOverArtists(selectedVO);
    setAddonsTotal(calculateTotal(selectedVO));
  };

  const onToggleChange = (value) => {
    if (value) {
      setArtistsPanelVisible(true);
      return;
    }

    resetAddonValuesByType('VOICE_OVER_ARTISTS');
    setArtistsPanelVisible(false);
    setVoiceOverInstructions('');
    setCustomAssets([]);
    const newOutputs = outputs.map((it) => {
      const { latestVersion } = it;

      return {
        ...it,
        voiceOverInstructions: '',
        voiceOverCustomAssets: [],
        versions: it.versions.map((version) => {
          const { versionNumber } = version;
          if (versionNumber !== latestVersion) return version;

          return {
            ...version,
            appliedAddons: {
              ...version.appliedAddons,
              VOICE_OVER_ARTISTS: []
            }
          };
        })
      };
    });
    onOutputsSave(newOutputs);
  };

  useEffect(() => {
    if (voiceOverArtists) {
      setSelectedVoiceOverArtists(voiceOverArtists);
      setAddonsTotal(calculateTotal(voiceOverArtists));
    }
  }, [voiceOverArtists]);

  const handleCustomAssets = async (files) => {
    if (!canUpdateVideo || _isEmpty(files)) {
      return;
    }

    const assetIds = await addFilesToQueue(
      files.map((file) => ({ file })),
      organisationId
    );

    const structuredAssetIds = await assetIds.map((assetId) => ({
      assetId,
      description: ''
    }));

    const newStructuredAssetIds = [
    ...(customAssets || []),
    ...structuredAssetIds];


    setCustomAssets((prevAssets) => [...prevAssets, ...structuredAssetIds]);

    if (isAnimationProject) {
      await updateVideoAtomic({
        type: 'ADD',
        fieldName: 'voiceOverCustomAssets',
        entityName: 'AnimationOutput',
        value: newStructuredAssetIds
      });
    } else {
      const newOutputs = outputs.map((it) => ({
        ...it,
        voiceOverCustomAssets: newStructuredAssetIds
      }));
      onOutputsSave(newOutputs);
    }

    updateProject(
      { assetIds: _uniq([...(projectAssets || []), ...assetIds]) },
      { override: true }
    );

    fileRef.current.map((current) => current.removeFiles()
    );
    fileRef.current = [];
  };

  const handleChangeCustomAssetDescription = async (
  assetId,
  { target: { value } }) =>
  {
    if (_isEmpty(customAssets)) return;

    if (customAssets.length) {
      const updatedCustomAssets = customAssets;
      const index = updatedCustomAssets.findIndex(
        (asset) => asset.assetId === assetId
      );
      if (index > -1) {
        updatedCustomAssets[index].description = value;
      }
      setCustomAssets(updatedCustomAssets);

      if (isAnimationProject) {
        await updateVideoAtomic({
          type: 'ADD',
          fieldName: 'voiceOverCustomAssets',
          entityName: 'AnimationOutput',
          value: updatedCustomAssets
        });
        return;
      }

      const newOutputs = outputs.map((it) => ({
        ...it,
        voiceOverCustomAssets: updatedCustomAssets
      }));
      onOutputsSave(newOutputs);
    }
  };

  const handleDeleteCustomAssets = async (assetId) => {
    const interm = [...customAssets];
    const deletedAssetId = interm.findIndex((asset) => asset.assetId === assetId);
    if (deletedAssetId >= 0) {
      interm.splice(deletedAssetId, 1);
      setCustomAssets(interm);

      if (isAnimationProject) {
        await updateVideoAtomic({
          type: 'ADD',
          fieldName: 'voiceOverCustomAssets',
          entityName: 'AnimationOutput',
          value: interm
        });
        return;
      }

      const newOutputs = outputs.map((it) => ({
        ...it,
        voiceOverCustomAssets: interm
      }));
      onOutputsSave(newOutputs);
    }
  };

  const handleOpenAssetDetailsModal = async (assetId) => {
    const selectedAssetAdditionalInfo = customAssets.filter(
      (asset) => asset.assetId === assetId
    );
    const data = await getAssets({ organisationId, assetIds: assetId });
    setSelectedCustomAssetDetail({
      ...selectedCustomAssetDetail,
      assetDetail: data.data.getAssets[0],
      additionalInfo: selectedAssetAdditionalInfo[0].description
    });
    setShowAssetDetailsModal(true);
  };

  const handleRemoveAssetFromProject = (deletedAssetId) => {
    handleDeleteCustomAssets(deletedAssetId);

    const intermProjectAssets = [...projectAssets];
    const index = intermProjectAssets.findIndex(
      (assetId) => assetId === deletedAssetId
    );

    if (index >= 0) {
      intermProjectAssets.splice(index, 1);
      updateProject(
        { assetIds: _uniq(intermProjectAssets) },
        { override: true }
      );
    }
  };

  const handleCloseAssetDetailsModal = () => {
    setSelectedCustomAssetDetail(null);
    setShowAssetDetailsModal(false);
  };

  const voiceOverSaveRef = useRef({ video, isAnimationProject, updateVideoAtomic, onOutputsSave });
  useEffect(() => {
    voiceOverSaveRef.current = { video, isAnimationProject, updateVideoAtomic, onOutputsSave };
  }, [video, isAnimationProject, updateVideoAtomic, onOutputsSave]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateVoiceOverInstructions = useCallback(_debounce((value) => {
    const {
      video: currentVideo,
      isAnimationProject: isAnimation,
      updateVideoAtomic: atomicUpdate,
      onOutputsSave: saveOutputs
    } = voiceOverSaveRef.current;
    const { outputs: videoOutputs, videoId } = currentVideo;

    if (isAnimation) {
      const inputs = {
        type: 'ADD',
        fieldName: 'voiceOverInstructions',
        entityName: 'AnimationOutput',
        value
      };
      atomicUpdate({ inputs, videoId });
      return;
    }

    const newOutputs = videoOutputs.map((output) => ({
      ...output,
      voiceOverInstructions: value
    }));

    saveOutputs(newOutputs);
  }, 500), []);

  const onChangeVoiceOverInstruction = ({ target: { value } }) => {
    setVoiceOverInstructions(value);
    updateVoiceOverInstructions(value);
  };

  const formattedCurrency = () => {
    const currencySymbol = CURRENCY_SYMBOLS[currencyCode];
    return `${currencyCode} ${currencySymbol}${addonsTotal}`;
  };

  const instructionPlaceholder = `Eg.
  • Read in a conversational, light hearted tone.
  • Ensure there are adequate pauses between paragraphs.
  • Please pronounce the word ‘colonel’ as ‘ker-nel’
  `;

  return (
    <div className="voice-over-v2-container">
      <ToggleValueAddon
        id="multi-voice-over-addon"
        title="Voice Over"
        subtitle="Estimated cost based on supplied script length and the number of voiceover artists selected."
        icon="voiceover"
        selected={artistsPanelVisible}
        onChange={onToggleChange}
        disabled={!canUpdateVideo}
        extraComponent={() =>
        <Link onClick={onInfoClick}>
            <Text heading4 className="toggle-addon__link">
              View rates
            </Text>
          </Link>}>


        <>
          <div className="voice-over-v2--artist-list-card">
            <div className="voice-over-v2--artist-list-card__list">
              <ManageVoiceOverArtists
                customAddonCosts={customAddonCosts}
                priceVersion={priceVersion}
                selectedVoiceOverArtists={selectedVoiceOverArtists || []}
                onSelect={handleSelectVO}
                scriptWordCount={scriptWordCount}
                canUpdateVideo={canUpdateVideo}
                isV2Design />

            </div>
            <div className="voice-over-v2--artist-list-card__footer">
              <Text
                heading4
                className="voice-over-v2--artist-list-card__footer--selected-count">

                Selected(
                {selectedVoiceOverArtists.length}
                )
              </Text>
              <div className="voice-over-v2--artist-list-card__footer--selected-amount">
                <Text
                  heading4
                  className="voice-over-v2--artist-list-card__footer--selected-amount--currency">

                  {formattedCurrency()}
                </Text>
                <div className="voice-over-v2--artist-list-card__footer--selected-amount--currency--info">
                  Estimated based on length of supplied script
                </div>
              </div>
            </div>
          </div>
          <div className="voice-over-v2--artist-list-card">
            <div className="voice-over-v2--artist-list-card--instructions">
              <span>Instructions for voice over artist</span>
            </div>
            <div className="voice-over-v2--artist-list-card--instructions--input">
              <Input
                className="voice-over-v2--artist-list-card--instructions--input--textarea"
                multiline
                rows={8}
                value={voiceOverInstructions}
                defaultValue={voiceOverInstructions}
                onChange={onChangeVoiceOverInstruction}
                placeholder={instructionPlaceholder}
                readOnly={!canUpdateVideo} />

            </div>
          </div>

          <div className="voice-over-v2--artist-list-card">
            <div className="voice-over-v2--custom-assets">
              <UploadVoiceOver
                addFilesToQueue={addFilesToQueue}
                organisationId={organisationId}
                fileRef={fileRef}
                getAssets={getAssets}
                customAssets={customAssets}
                handleCustomAssets={handleCustomAssets}
                handleChangeCustomAssetDescription={
                handleChangeCustomAssetDescription}

                handleDeleteCustomAssets={handleDeleteCustomAssets}
                handleOpenAssetDetailsModal={handleOpenAssetDetailsModal}
                handleRemoveAssetFromProject={handleRemoveAssetFromProject}
                canUpdateVideo={canUpdateVideo}
                assetsMap={assetsMap} />

            </div>
          </div>
          {showAssetDetailsModal &&
          <div className="voice-over-v2--artist-list-card">
              <div className="voice-over-v2--custom-assets">
                <AssetDetailsModal
                showAssetDetailsModal={showAssetDetailsModal}
                closeModal={handleCloseAssetDetailsModal}
                assetDetail={selectedCustomAssetDetail}
                handleChangeCustomAssetDescription={
                handleChangeCustomAssetDescription} />


              </div>
            </div>}

        </>
      </ToggleValueAddon>
    </div>);

}